import type { DragPositionState, MenuFixedLevels } from './types';
import type { Ref } from 'vue';

import { reactive, ref } from 'vue';

import { DRAG_CONSTANTS } from './constants';

export function useDragCalculations(
	menuFixedLevels: Ref<MenuFixedLevels>,
	dragRef: Ref<HTMLElement | null>
) {
	// Drag position state
	const positionState = reactive<DragPositionState>({
		yOnDrag: 0,
		initialMouseY: 0,
		currentMovement: 0,
		actualMovement: 0
	});

	function initializeDragWhenNeeded(dragging: boolean, initialY: number): void {
		if (dragging && positionState.yOnDrag === 0) {
			positionState.initialMouseY = initialY;

			// ALWAYS get the current DOM element's actual position
			if (dragRef.value) {
				const transform = window.getComputedStyle(dragRef.value).transform;
				const matrix = new DOMMatrix(transform);
				const currentTransformY = Math.max(0, matrix.m42 || 0); // m42 is translateY

				positionState.yOnDrag = currentTransformY;
			} else {
				// Fallback for currentY if dragRef is not available
				positionState.yOnDrag = Math.max(0, 0);
			}
		}
	}

	function calculateRawMovement(y: number, isDraggingUp: boolean): number {
		return isDraggingUp ? Math.abs(y) : Math.max(0, y);
	}

	function calculateActualMovement(
		isDraggingUp: boolean,
		rawMovement: number
	): number {
		if (isDraggingUp) {
			const calculatedMovement = Math.max(
				0,
				positionState.yOnDrag - rawMovement
			);
			// Force exactly 0 when within 1 pixel to prevent visual gaps
			return calculatedMovement <= DRAG_CONSTANTS.POSITION_SNAP_THRESHOLD
				? 0
				: calculatedMovement;
		} else {
			// Use the smallest value from menuFixedLevels to calculate the limit
			const menuHeights = Object.values(menuFixedLevels.value);
			const smallestMenuHeight =
				menuHeights.length > 0 ? Math.min(...menuHeights) : 0;

			return Math.min(
				window.innerHeight - smallestMenuHeight,
				positionState.yOnDrag + rawMovement
			);
		}
	}

	function applyDragLimits(
		actualMovement: number,
		isDraggingUp: boolean,
		isDragging: boolean
	): boolean {
		const screenHeight = window.innerHeight;

		if (isDraggingUp && actualMovement <= 0) {
			return true; // Limit reached
		}

		if (!isDraggingUp) {
			// Use the smallest value from menuFixedLevels to calculate maxLimit
			const menuHeights = Object.values(menuFixedLevels.value);
			const smallestMenuHeight =
				menuHeights.length > 0 ? Math.min(...menuHeights) : 0;

			const maxLimit = screenHeight - smallestMenuHeight;
			if (actualMovement >= maxLimit) {
				return true; // Limit reached
			}
		}

		return false; // No limit reached
	}

	function isSmallMovement(y: number): boolean {
		return (
			(y >= 0 && y < DRAG_CONSTANTS.CLICK_THRESHOLD) ||
			(y <= 0 && y > -DRAG_CONSTANTS.CLICK_THRESHOLD)
		);
	}

	function resetDragPosition(): void {
		positionState.yOnDrag = 0;
		positionState.initialMouseY = 0;
		positionState.currentMovement = 0;
		positionState.actualMovement = 0;
	}

	function updateDragPosition(movement: number, actualMovement: number): void {
		// Force exactly 0 when within threshold to prevent visual gaps
		const exactMovement =
			actualMovement <= DRAG_CONSTANTS.POSITION_SNAP_THRESHOLD
				? 0
				: actualMovement;

		positionState.currentMovement = movement;
		positionState.actualMovement = exactMovement;
	}

	function reinitializeDragIfNeeded(dragging: boolean, currentY: number): void {
		// Reinitialize yOnDrag if it was reset during scroll mode
		if (dragging && positionState.yOnDrag === 0) {
			positionState.yOnDrag = Math.max(0, currentY);
		}
	}

	return {
		// State
		positionState: ref(positionState),

		// Actions
		initializeDragWhenNeeded,
		calculateRawMovement,
		calculateActualMovement,
		applyDragLimits,
		isSmallMovement,
		resetDragPosition,
		updateDragPosition,
		reinitializeDragIfNeeded
	};
}
